import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiError } from "@/lib/api";
import {
  AnchoredDetailCallout,
  buildAttributeAnchorSelector,
} from "@/components/ui/AnchoredDetailCallout";
import {
  AnchoredDetailCalloutBody,
  AnchoredDetailCalloutFooter,
  AnchoredDetailCalloutScrollRegion,
  DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_MIN_HEIGHT_PX,
  FORM_ANCHORED_DETAIL_CALLOUT_SHELL_CLASS,
} from "@/components/ui/AnchoredDetailFormCallout";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Input } from "@/components/ui/Input";
import { HoverHintTrigger, HoverHintWithGlyph, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import { formSnapshotDirty } from "@/lib/formDirty";
import {
  formatNumber,
  MRP_LOCATIONS,
  mrpLocationChipStyles,
  type MrpLocation,
  type MrpSkuDetail,
} from "@/lib/mrp";

export type MrpReserveReplenishField = "reserve" | "replenish";

export type MrpReserveReplenishSavedPayload = {
  sku: string;
  reserveChanges: Partial<Record<MrpLocation, number | null>>;
};

export const MRP_RESERVE_REPLENISH_CALLOUT_WIDTH_PX = 30 * 16;
/** Room for title, three location rows, two numeric columns, and footer buttons when positioning the callout. */
export const MRP_RESERVE_REPLENISH_CALLOUT_MIN_CONTENT_HEIGHT_PX = Math.max(
  DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_MIN_HEIGHT_PX,
  22 * 16,
);

export const MRP_FORECAST_RESERVE_GLYPH_ATTR = "data-forecast-reserve-glyph";
export const MRP_SAFETY_STOCK_RESERVE_CELL_ATTR = "data-safety-stock-cell";

export function buildMrpReserveReplenishAnchorKey(
  field: MrpReserveReplenishField,
  sku: string,
  locationCode: string,
): string {
  return `${field}:${sku}:${locationCode}`;
}

function parseOptionalInt(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isInteger(parsed) ? parsed : undefined;
}

type MrpReserveReplenishDraft = {
  reserve: Record<MrpLocation, string>;
  reorderTo: Record<MrpLocation, string>;
};

function reserveValuesFromDetail(detail: MrpSkuDetail): Record<MrpLocation, string> {
  const reserves = detail.mrp_reserve_by_location ?? {};
  return Object.fromEntries(
    MRP_LOCATIONS.map(({ code }) => [code, reserves[code] != null ? String(reserves[code]) : ""]),
  ) as Record<MrpLocation, string>;
}

function reorderToValuesFromDetail(detail: MrpSkuDetail): Record<MrpLocation, string> {
  const replen = detail.mrp_replenish_to_by_location ?? {};
  return Object.fromEntries(
    MRP_LOCATIONS.map(({ code }) => [code, replen[code] != null ? String(replen[code]) : ""]),
  ) as Record<MrpLocation, string>;
}

function draftFromDetail(detail: MrpSkuDetail): MrpReserveReplenishDraft {
  return {
    reserve: reserveValuesFromDetail(detail),
    reorderTo: reorderToValuesFromDetail(detail),
  };
}

function buildCombinedReserveReplenishPatch(
  detail: MrpSkuDetail,
  draft: MrpReserveReplenishDraft,
): Record<string, unknown> {
  const reservePatch = buildReserveReplenishPatch(detail, "reserve", draft.reserve);
  const effectiveReserve: Record<string, number | null | undefined> = {
    ...(detail.mrp_reserve_by_location ?? {}),
  };
  const reserveDelta = reservePatch.mrp_reserve_by_location as Record<string, number | null> | undefined;
  if (reserveDelta != null) {
    for (const [code, value] of Object.entries(reserveDelta)) {
      if (value === null) delete effectiveReserve[code];
      else effectiveReserve[code] = value;
    }
  }
  const zeroReservePatch: Record<string, number> = {};
  for (const { code } of MRP_LOCATIONS) {
    const reorderTo = parseOptionalInt(draft.reorderTo[code] ?? "");
    if (reorderTo != null && reorderTo > 0 && effectiveReserve[code] == null) {
      zeroReservePatch[code] = 0;
      effectiveReserve[code] = 0;
    }
  }
  const detailWithReserve: MrpSkuDetail = {
    ...detail,
    mrp_reserve_by_location: effectiveReserve as MrpSkuDetail["mrp_reserve_by_location"],
  };
  const reorderPatch = buildReserveReplenishPatch(detailWithReserve, "replenish", draft.reorderTo);
  const mergedReserve = { ...(reserveDelta ?? {}), ...zeroReservePatch };
  return {
    ...(Object.keys(mergedReserve).length > 0 ? { mrp_reserve_by_location: mergedReserve } : {}),
    ...reorderPatch,
  };
}

function buildReserveReplenishPatch(
  detail: MrpSkuDetail,
  field: MrpReserveReplenishField,
  values: Record<MrpLocation, string>,
): Record<string, unknown> {
  if (field === "reserve") {
    const previous = detail.mrp_reserve_by_location ?? {};
    const patch: Record<string, number | null> = {};
    let changed = false;
    for (const { code } of MRP_LOCATIONS) {
      const parsed = parseOptionalInt(values[code] ?? "");
      if (parsed === undefined) {
        throw new Error(`Reserve (${code}) must be an integer or empty.`);
      }
      const next = parsed;
      const prior = previous[code];
      if (next !== (prior === undefined ? null : prior)) {
        changed = true;
        patch[code] = next;
      }
    }
    return changed ? { mrp_reserve_by_location: patch } : {};
  }

  const reserveByLocation = detail.mrp_reserve_by_location ?? {};
  const previousReplenish = detail.mrp_replenish_to_by_location ?? {};
  const patch: Record<string, number | null> = {};
  let changed = false;
  for (const { code } of MRP_LOCATIONS) {
    const parsed = parseOptionalInt(values[code] ?? "");
    if (parsed === undefined) {
      throw new Error(`Replenish to (${code}) must be an integer or empty.`);
    }
    const next = parsed === null || parsed === 0 ? null : parsed;
    const priorRaw = previousReplenish[code];
    const prior = priorRaw != null && priorRaw > 0 ? priorRaw : null;
    const reserve = reserveByLocation[code];
    const adjustedNext =
      next != null && reserve != null && next < reserve ? reserve : next;
    if (adjustedNext !== prior) {
      changed = true;
      patch[code] = adjustedNext;
    }
  }
  return changed ? { mrp_replenish_to_by_location: patch } : {};
}

const RESERVE_COLUMN_HINT =
  "Safety stock reserve per planning site. Empty clears the stored value for that site. Zero is an explicit zero reserve.";
const REORDER_TO_COLUMN_HINT =
  "Target level for suggested order quantity. Empty clears the override (effective value defaults to twice the reserve). When reserve is empty, reorder to alone stores reserve as zero.";

function MrpReserveReplenishPanel(props: {
  sku: string;
  field: MrpReserveReplenishField;
  hubWritesAllowed: boolean;
  onClose: () => void;
  onSaved?: (payload: MrpReserveReplenishSavedPayload) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<MrpReserveReplenishDraft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const detailQuery = useQuery<MrpSkuDetail, ApiError>({
    queryKey: ["mrp", "skus", props.sku],
    queryFn: () => apiFetch<MrpSkuDetail>("/api/mrp/skus", { query: { sku: props.sku } }),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (detailQuery.data == null) {
      setDraft(null);
      return;
    }
    setDraft(draftFromDetail(detailQuery.data));
    setSaveError(null);
  }, [detailQuery.data, props.sku]);

  const saveMutation = useMutation<void, ApiError, MrpReserveReplenishDraft>({
    mutationFn: async (values) => {
      const detail = detailQuery.data;
      if (detail == null) {
        throw new ApiError(400, "SKU detail is not loaded yet.");
      }
      const patch = buildCombinedReserveReplenishPatch(detail, values);
      if (Object.keys(patch).length === 0) {
        return;
      }
      await apiFetch<MrpSkuDetail>("/api/mrp/skus", {
        method: "PATCH",
        query: { sku: props.sku },
        body: patch,
      });
    },
    onSuccess: (_data, draft) => {
      const detail = detailQuery.data;
      if (detail != null) {
        const patch = buildCombinedReserveReplenishPatch(detail, draft);
        const reserveChanges =
          (patch.mrp_reserve_by_location as Partial<Record<MrpLocation, number | null>> | undefined) ?? {};
        if (Object.keys(reserveChanges).length > 0) {
          props.onSaved?.({ sku: props.sku, reserveChanges });
        }
      }
      void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus", props.sku] });
      props.onClose();
    },
    onError: (error) => {
      setSaveError(error.message);
    },
  });

  const title =
    props.field === "reserve" ? "Set reserve by location" : "Set reorder to by location";

  return (
    <>
      <AnchoredDetailCalloutBody className="pb-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</p>
          <p className="font-mono text-sm font-semibold text-slate-900">{props.sku}</p>
        </div>
      </AnchoredDetailCalloutBody>
      <AnchoredDetailCalloutScrollRegion>
        {detailQuery.isLoading ? (
          <p className="text-sm text-slate-600">Loading…</p>
        ) : detailQuery.isError ? (
          <p className="text-sm text-red-700">{detailQuery.error.message}</p>
        ) : draft == null ? (
          <p className="text-sm text-slate-600">No values loaded.</p>
        ) : (
          <ReadOnlyHoverLayer
            active={!props.hubWritesAllowed}
            hoverHint="You do not have permission to edit MRP reserve and reorder settings."
            layout="block"
          >
            <div className="space-y-2">
              <div className="grid grid-cols-[minmax(0,1fr)_6.5rem_6.5rem] items-center gap-2">
                <span className="text-xs text-slate-500">Location</span>
                <div className="flex items-center justify-end gap-1">
                  <span className="text-xs text-slate-600">Reserve</span>
                  <HoverHintWithGlyph size="sm" hoverHint={RESERVE_COLUMN_HINT} />
                </div>
                <div className="flex items-center justify-end gap-1">
                  <span className="text-xs text-slate-600">Reorder to</span>
                  <HoverHintWithGlyph size="sm" hoverHint={REORDER_TO_COLUMN_HINT} />
                </div>
              </div>
              {MRP_LOCATIONS.map(({ code, label }) => {
                const chip = mrpLocationChipStyles(code);
                return (
                  <div key={code} className="grid grid-cols-[minmax(0,1fr)_6.5rem_6.5rem] items-center gap-2">
                    <span
                      className={cn(
                        "inline-flex w-fit rounded border px-2 py-0.5 text-xs font-medium",
                        chip.className,
                      )}
                    >
                      {label}
                    </span>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      className="h-9 text-right tabular-nums"
                      value={draft.reserve[code]}
                      disabled={!props.hubWritesAllowed || saveMutation.isPending}
                      aria-label={`Reserve for ${label}`}
                      onChange={(event) => {
                        setSaveError(null);
                        setDraft((previous) =>
                          previous == null
                            ? previous
                            : {
                                ...previous,
                                reserve: { ...previous.reserve, [code]: event.target.value },
                              },
                        );
                      }}
                    />
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      className="h-9 text-right tabular-nums"
                      value={draft.reorderTo[code]}
                      disabled={!props.hubWritesAllowed || saveMutation.isPending}
                      aria-label={`Reorder to for ${label}`}
                      onChange={(event) => {
                        setSaveError(null);
                        setDraft((previous) =>
                          previous == null
                            ? previous
                            : {
                                ...previous,
                                reorderTo: { ...previous.reorderTo, [code]: event.target.value },
                              },
                        );
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </ReadOnlyHoverLayer>
        )}
        {saveError != null ? <p className="mt-3 text-sm text-red-700">{saveError}</p> : null}
      </AnchoredDetailCalloutScrollRegion>
      <AnchoredDetailCalloutFooter>
        <Button type="button" variant="ghost" size="sm" onClick={props.onClose} disabled={saveMutation.isPending}>
          Cancel
        </Button>
        <HubSaveButton
          type="button"
          size="sm"
          dirty={
            draft != null &&
            detailQuery.data != null &&
            formSnapshotDirty(draft, draftFromDetail(detailQuery.data))
          }
          disabled={
            !props.hubWritesAllowed ||
            draft == null ||
            detailQuery.isLoading ||
            detailQuery.isError
          }
          blockedHint="You do not have permission to edit MRP reserve and reorder settings."
          isLoading={saveMutation.isPending}
          onClick={() => {
            if (draft == null) return;
            setSaveError(null);
            saveMutation.mutate(draft);
          }}
        >
          Save
        </HubSaveButton>
      </AnchoredDetailCalloutFooter>
    </>
  );
}

export function MrpReserveReplenishCallout({
  sku,
  field,
  anchorKey,
  anchorAttribute,
  anchorElement = null,
  hubWritesAllowed,
  onClose,
  onSaved,
}: {
  sku: string;
  field: MrpReserveReplenishField;
  anchorKey: string;
  anchorAttribute: string;
  anchorElement?: HTMLElement | null;
  hubWritesAllowed: boolean;
  onClose: () => void;
  onSaved?: (payload: MrpReserveReplenishSavedPayload) => void;
}): JSX.Element | null {
  return (
    <AnchoredDetailCallout
      anchorSelector={buildAttributeAnchorSelector(anchorAttribute, anchorKey)}
      anchorElement={anchorElement}
      onClose={onClose}
      panelWidth={MRP_RESERVE_REPLENISH_CALLOUT_WIDTH_PX}
      minContentHeight={MRP_RESERVE_REPLENISH_CALLOUT_MIN_CONTENT_HEIGHT_PX}
      ariaLabel="Edit reserve and replenish targets"
      measureKey={anchorKey}
      scrollMode="none"
      contentClassName={FORM_ANCHORED_DETAIL_CALLOUT_SHELL_CLASS}
    >
      <MrpReserveReplenishPanel
        sku={sku}
        field={field}
        hubWritesAllowed={hubWritesAllowed}
        onClose={onClose}
        onSaved={onSaved}
      />
    </AnchoredDetailCallout>
  );
}

export function MrpReserveReplenishGlyph({
  sku,
  locationLabel,
  locationReserve,
  anchorKey,
  onOpen,
}: {
  sku: string;
  locationLabel: string;
  locationReserve: number;
  anchorKey: string;
  onOpen: (sku: string, anchorKey: string) => void;
}): JSX.Element {
  const reserveLabel = formatNumber(locationReserve);
  const hoverHint = `Reserve ${reserveLabel} at ${locationLabel}. Click to edit.`;

  return (
    <HoverHintTrigger
      trigger={
        <button
          type="button"
          data-forecast-reserve-glyph={anchorKey}
          aria-label={`Reserve ${reserveLabel} for ${sku} at ${locationLabel}. Click to edit.`}
          className={cn(
            "inline-flex shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[11px] font-medium tabular-nums",
            "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1",
            locationReserve > 0
              ? "border-slate-200 bg-slate-100 text-slate-700 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-800"
              : "border-slate-200 bg-slate-50 text-slate-500 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-800",
          )}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpen(sku, anchorKey);
          }}
        >
          {reserveLabel}
        </button>
      }
    >
      <span className="block break-words">{hoverHint}</span>
    </HoverHintTrigger>
  );
}
